import React, { ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import { Crisp } from 'crisp-sdk-web';
import { useAuth } from 'components/Auth/AuthProvider';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_FUB_BY_ID } from 'apolloClient/queries/followUpBoss';
import { CRISP_CHAT_TOKEN } from 'src/constants';
import {
  SEND_CRISP_MSGS_AS_NOTES_TO_FUB,
  UPDATE_FUB_USER,
} from 'apolloClient/mutations/followUpBoss';
import {
  wasOnceLogged,
  getbrowserUUIDFromLocalStorage,
  getLeadIdFromLocalStorage,
  saveLeadIdToLocalStorage,
} from 'src/utils/utils';
import { consumeCrispResetPending, isLoginAsUser } from 'src/utils/authUtils';
import { usePPC } from 'components/PPCProvider';
import { pushCrispChatLeadEvent } from 'src/gtm';

let tempMessage: unknown[] = [];

export default function withCrispChat<T extends object>(Component: ComponentType<T>) {
  return function ComponentWithCrispChat(props: T) {
    const [messages, setMessages] = useState<unknown[]>([]);
    const [initialFubAssign, setInitialFubAssign] = useState<boolean>(true);
    const [sessionId, setSessionId] = useState('');
    const messageHandlersRegistered = useRef(false);
    const { isLogged, me } = useAuth();
    const [getFubById] = useLazyQuery(GET_FUB_BY_ID);
    const [updateFubUser] = useMutation(UPDATE_FUB_USER);
    const [sendCrispMsgsToFub] = useMutation(SEND_CRISP_MSGS_AS_NOTES_TO_FUB);
    const { isPPCCookie } = usePPC();
    // Crisp callbacks are registered once per chat open, so read PPC state via ref
    const isPPCRef = useRef(isPPCCookie);
    isPPCRef.current = isPPCCookie;
    // dedup fallback for when localStorage writes fail
    const trackedLeadSessions = useRef(new Set<string>());

    useEffect(() => {
      // Crisp's session is browser-wide, so here it would mix the admin's chat
      // identity with the client's FUB lead.
      if (isLoginAsUser()) return;

      Crisp.configure(CRISP_CHAT_TOKEN, {
        safeMode: true,
      });

      if (consumeCrispResetPending()) Crisp.session.reset();

      Crisp.chat.onChatOpened(() => {
        registerCrispHandlers();
      });
    }, []);

    const SendCrispMessagesToFub = useCallback(
      (msgs = messages) => {
        if (!msgs.length) return;
        const leadId = getLeadIdFromLocalStorage();
        if (!leadId) return;

        sendCrispMsgsToFub({
          variables: {
            fubId: +leadId,
            messages: msgs,
          },
        }).finally(() => {
          setMessages([]);
        });
      },
      [messages, sendCrispMsgsToFub]
    );

    if (typeof window === 'undefined') return <Component {...props} />;

    const wasLoggedBefore = localStorage && wasOnceLogged();

    async function AssignFubLeadDataToCrisp() {
      const leadId = getLeadIdFromLocalStorage();
      if (!leadId || !initialFubAssign) return;

      setInitialFubAssign(false);

      const {
        data: { getPeopleById: fubUser },
      } = await getFubById({
        variables: {
          id: +leadId,
        },
      });

      if (!fubUser) return;

      if (fubUser.emails.length > 0) {
        Crisp.user.setEmail(fubUser.emails[0].value);
      }

      if (fubUser.phones.length > 0) {
        Crisp.user.setPhone(fubUser.phones[0].value);
      }

      if (fubUser.name.length > 0) {
        Crisp.user.setNickname(fubUser.name[0].value);
      }

      const leadName =
        fubUser.name != null
          ? fubUser.name
          : fubUser.emails.length > 0
          ? fubUser.emails[0].value
          : fubUser.phones.length > 0
          ? fubUser.phones[0].value
          : '';

      Crisp.session.setData([
        [
          [
            'lead_fub_assignment',
            `${fubUser.assignedTo} is assigned to ${leadName} in FUB. https://hbroswell2.followupboss.com/2/people/view/${fubUser.id}`,
          ],
        ],
      ]);
      SendCrispMessagesToFub();

      setSessionId(Crisp.session.getIdentifier() ?? '');
    }

    function updateFubLead() {
      const leadId = getLeadIdFromLocalStorage() || 0;

      const email = Crisp.user.getEmail();
      const phone = Crisp.user.getPhone();
      const nickname = Crisp.user.getNickname();
      const nicknameIsPhone =
        nickname != null &&
        nickname.startsWith('+') &&
        !isNaN(+nickname.substring(1));
      const phoneToUse =
        phone != null ? phone : nicknameIsPhone ? nickname : null;

      const browserUUID = getbrowserUUIDFromLocalStorage() || undefined;


      const pendingMessages = tempMessage;
      tempMessage = [];

      updateFubUser({
        variables: {
          fubId: +leadId,
          email: email,
          phone: phoneToUse,
          name: nickname,
          browserUUID,
        },
      }).then((data) => {
        if (data?.data?.updateFollowUpBossUser) {
          const updatedLeadId = data?.data?.updateFollowUpBossUser?.id;
          const fubLink = data?.data?.updateFollowUpBossUser?.fubLink;

          // Update the leadId in localStorage if it has changed
          if (updatedLeadId && leadId !== updatedLeadId.toString()) {
            saveLeadIdToLocalStorage(updatedLeadId.toString());
          }

          if (fubLink) {
            Crisp.session.setData({ fub_link: fubLink });
          }

          if (pendingMessages.length > 0) {
            sendCrispMsgsToFub({
              variables: {
                fubId: +updatedLeadId,
                messages: pendingMessages,
              },
            }).finally(() => {
              setMessages([]);
            });
          }

          if (tempMessage.length > 0) {
            const latePending = tempMessage;
            tempMessage = [];
            sendCrispMsgsToFub({
              variables: {
                fubId: +updatedLeadId,
                messages: latePending,
              },
            });
          }
        }
      });
    }

    // best-effort: analytics must never block the FUB update that follows
    function trackChatLead(contact: string) {
      try {
        // visitors with a FUB lead (forms, sign-up, earlier chats) aren't new leads
        if (!contact || getLeadIdFromLocalStorage()) return;
        const dedupKey = `crisp_chat_lead_${Crisp.session.getIdentifier() ?? ''}`;
        const push = () => {
          if (trackedLeadSessions.current.has(dedupKey)) return;
          trackedLeadSessions.current.add(dedupKey);
          try {
            if (localStorage.getItem(dedupKey)) return;
            localStorage.setItem(dedupKey, '1');
          } catch {}
          pushCrispChatLeadEvent(
            Crisp.user.getEmail() ?? '',
            Crisp.user.getPhone() ?? '',
            isPPCRef.current ? 'ppc' : 'organic'
          );
        };
        // one goal per chat session, even with the site open in several tabs
        if (typeof navigator === 'undefined' || !navigator.locks) push();
        else navigator.locks.request(dedupKey, push);
      } catch {}
    }

    function onEmailChanged(email: string) {
      trackChatLead(email);
      if (!isLogged && !wasLoggedBefore) {
        updateFubLead();
      }
    }
    function onPhoneChanged(phone: string) {
      trackChatLead(phone);
      if (!isLogged && !wasLoggedBefore) {
        updateFubLead();
      }
    }
    function onNicknameChanged(nickname: string) {
      if (!isLogged && !wasLoggedBefore) {
        updateFubLead();
      }
    }

    function sendMessageToFub(message: unknown) {
      const leadId = getLeadIdFromLocalStorage();
      setMessages((prev) => [...prev, message]);
      if (leadId) {
        SendCrispMessagesToFub([message]);
      } else {
        tempMessage = [...tempMessage, message];
      }
    }

    function withTabLock(message: unknown, send: () => void) {
      const fp = (message as any)?.fingerprint;
      if (!fp || typeof navigator === 'undefined' || !navigator.locks) {
        send();
        return;
      }
      const dedupKey = `crisp_fub_sent_${fp}`;
      // Use Web Locks as a serializing mutex so the localStorage read-check-set
      // is atomic across tabs. The first tab to enter sets the key and sends;
      // all subsequent tabs see the key and skip.
      navigator.locks.request(`crisp_fub_${fp}`, async () => {
        if (localStorage.getItem(dedupKey)) return;
        localStorage.setItem(dedupKey, '1');
        setTimeout(() => localStorage.removeItem(dedupKey), 30_000);
        send();
      });
    }

    function onMessageSent(message: unknown) {
      withTabLock(message, () => sendMessageToFub(message));
    }
    function onMessageReceived(message: unknown) {
      withTabLock(message, () => {
        setMessages((prev) => [...prev, message]);
        SendCrispMessagesToFub([message]);
      });
    }

    function registerCrispHandlers() {
      Crisp.session.setData([[['current_url', location.href]]]);
      AssignFubLeadDataToCrisp();

      Crisp.user.onEmailChanged(onEmailChanged);
      Crisp.user.onPhoneChanged(onPhoneChanged);
      Crisp.user.onNicknameChanged(onNicknameChanged);

      if (!messageHandlersRegistered.current) {
        Crisp.message.onMessageSent(onMessageSent);
        Crisp.message.onMessageReceived(onMessageReceived);
        messageHandlersRegistered.current = true;
      }
    }

    return <Component {...props} />;
  };
}
